/** Timestamp formatting. Server timestamps are RFC 3339 / TIMESTAMPTZ strings. */

const absoluteFormat = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "numeric",
});

const preciseFormat = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const relativeFormat = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

function parse(iso: string): Date | null {
  if (iso === "") {
    return null;
  }
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDate(iso: string): string {
  const date = parse(iso);
  return date === null ? "—" : absoluteFormat.format(date);
}

export function formatDateTime(iso: string): string {
  const date = parse(iso);
  return date === null ? "—" : preciseFormat.format(date);
}

/** "3 days ago" for anything within a month, an absolute date beyond that. */
export function formatRelative(iso: string, now: Date = new Date()): string {
  const date = parse(iso);
  if (date === null) {
    return "—";
  }

  const delta = date.getTime() - now.getTime();
  const magnitude = Math.abs(delta);

  if (magnitude < MINUTE) {
    return "just now";
  }
  if (magnitude < HOUR) {
    return relativeFormat.format(Math.round(delta / MINUTE), "minute");
  }
  if (magnitude < DAY) {
    return relativeFormat.format(Math.round(delta / HOUR), "hour");
  }
  if (magnitude < WEEK) {
    return relativeFormat.format(Math.round(delta / DAY), "day");
  }
  if (magnitude < 4 * WEEK) {
    return relativeFormat.format(Math.round(delta / WEEK), "week");
  }
  return absoluteFormat.format(date);
}
